import { addDays, format, isAfter } from 'date-fns'
import { v4 as getRandomId } from 'uuid'
import theme, { slotColors, routePlanningColors } from 'lib/theme'
import {
  DeliveryAreaGroup,
  SpecificDeliveryAreaGroup,
} from 'types/model/common'
import { SelectedAreaProps } from 'providers/ServiceAreasProvider'
import { DeliveryMethod } from 'types/api/serviceAreas'

const getColorTheme = (
  orderQuotaPercentage: number,
  deliverySlotDate: Date
) => {
  if (isAfter(new Date().setHours(0, 0, 0, 0), deliverySlotDate)) {
    if (orderQuotaPercentage === 100) {
      return {
        backgroundColor: slotColors.inThePast.full,
        color: theme.palette.commonColors.white,
        border: `1px solid ${theme.palette.commonColors.white}`,
      }
    }
    if (orderQuotaPercentage < 100 && orderQuotaPercentage >= 50) {
      return {
        backgroundColor: slotColors.inThePast.almostFull,
        color: theme.palette.commonColors.white,
        border: `1px solid ${theme.palette.commonColors.white}`,
      }
    }
    if (orderQuotaPercentage < 50 && orderQuotaPercentage > 0) {
      return {
        backgroundColor: slotColors.inThePast.halfway,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.text.primary}`,
      }
    }
    if (orderQuotaPercentage === 0 || !orderQuotaPercentage) {
      return {
        backgroundColor: slotColors.inThePast.empty,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.text.primary}`,
      }
    }
  }
  if (orderQuotaPercentage === 100) {
    return {
      backgroundColor: slotColors.full,
      color: theme.palette.commonColors.white,
      border: `1px solid ${theme.palette.commonColors.white}`,
    }
  }
  if (orderQuotaPercentage < 100 && orderQuotaPercentage >= 50) {
    return {
      backgroundColor: slotColors.almostFull,
      color: theme.palette.commonColors.white,
      border: `1px solid ${theme.palette.commonColors.white}`,
    }
  }
  if (orderQuotaPercentage < 50 && orderQuotaPercentage > 0) {
    return {
      backgroundColor: slotColors.halfway,
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}`,
    }
  }
  if (orderQuotaPercentage === 0) {
    return {
      backgroundColor: slotColors.empty,
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}`,
    }
  }
  return {
    backgroundColor: slotColors.empty,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
  }
}

export const getColorFromIndex = (colorIndex: number, colors?: string[]) => {
  const availableColors = colors || routePlanningColors
  return colorIndex > availableColors.length
    ? availableColors[colorIndex % availableColors.length]
    : availableColors[colorIndex]
}

export const getPolygonColor = (
  id: number,
  allDeliveryAreas: DeliveryAreaGroup[]
) => {
  const colorIndex =
    allDeliveryAreas.findIndex((deliveryArea) => deliveryArea.id === id) || 0
  return getColorFromIndex(colorIndex)
}

export const hasNoDeliveryAreas = (
  coordinateGroups: SpecificDeliveryAreaGroup[][],
  selectedDeliveryAreaGroup: SelectedAreaProps
) => {
  return (
    coordinateGroups.filter(
      (coordinateGroup) =>
        coordinateGroup[0].deliveryAreaGroupId === selectedDeliveryAreaGroup.id
    ).length === 0
  )
}

// TODO: fix when endpoint returns order count
export function eventStyleGetter(event: { title: string }, end: Date) {
  // const orderCounts = event.title.split('\n')[1]
  // const percentage =
  //   (Number(orderCounts.split('/')[0]) / Number(orderCounts.split('/')[1])) *
  //   100
  const style = {
    ...getColorTheme(0, end),
    borderRadius: '2px',
    display: 'block',
  }
  return {
    style,
  }
}

// calculate center of polygon for infobox, calculations factor in if polygon is irregular and concave
export const getPolygonCentroid = (pts: { lat: number; lng: number }[]) => {
  const first = pts[0]
  const last = pts[pts.length - 1]
  if (first.lat !== last.lat || first.lng !== last.lng) pts.push(first)
  let twicearea = 0
  let x = 0
  let y = 0
  const nPts = pts.length
  let p1
  let p2
  let f
  // eslint-disable-next-line no-plusplus
  for (let i = 0, j = nPts - 1; i < nPts; j = i++) {
    p1 = pts[i]
    p2 = pts[j]
    f = p1.lat * p2.lng - p2.lat * p1.lng
    twicearea += f
    x += (p1.lat + p2.lat) * f
    y += (p1.lng + p2.lng) * f
  }
  f = twicearea * 3
  return [x / f, y / f]
}

export const checkValidityStart = (
  date: string,
  validityStart: string,
  startOfWeek: Date
) => {
  const formattedDate =
    date.length > 1
      ? date
      : format(addDays(new Date(startOfWeek), Number(date)), 'yyyy-MM-dd')
  return new Date(validityStart) <= new Date(formattedDate)
}

export const createDeliveryMethodPayload = (
  type: 'pickup' | 'homedelivery',
  currencyCode: string,
  name?: string,
  code?: string
) => {
  return {
    guid: getRandomId(),
    name: type === 'pickup' ? name : 'Home Delivery',
    details: {
      provider_id: getRandomId(),
      type: 'foodie',
      for_dropshipping: false,
      configurations: [],
      services: [
        {
          name: type === 'pickup' ? name : 'Home Delivery',
          code: type === 'pickup' ? code : 'delivery',
          type,
          price: {
            currency: currencyCode,
            min: 0,
            max: 100.1,
          },
        },
      ],
    },
  } as DeliveryMethod
}
